import React, { useState, useRef } from 'react';
import { uploadMedia } from '../../services/community.service';

/**
 * Pictures and videos for a My Hive post or comment (Jenn, 2026-10-24):
 * up to 10, videos up to 3 minutes, each with an optional "Describe this
 * picture" box (our alt-text rule). Drag in, paste, or tap to choose several
 * at once. Each file uploads as soon as it's picked; the parent keeps the list
 * (`items`, `setItems`) and sends mediaPayload(items) when posting.
 */
export const MAX_MEDIA = 10;
const MAX_VIDEO_SECONDS = 180;

export const mediaUploading = (items) => items.some(i => i.status === 'uploading');
export const mediaPayload = (items) => items.filter(i => i.status === 'done').map(i => ({ id: i.id, alt_text: i.alt_text || '' }));

const videoLength = (file) => new Promise((resolve) => {
  const v = document.createElement('video');
  v.preload = 'metadata';
  const url = URL.createObjectURL(file);
  const done = (val) => { URL.revokeObjectURL(url); resolve(val); };
  v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? v.duration : null);
  v.onerror = () => done(null);
  setTimeout(() => done(null), 8000);
  v.src = url;
});

let keySeq = 0;

export default function MediaPicker({ items, setItems, compact = false }) {
  const [dragging, setDragging] = useState(false);
  const [note, setNote] = useState(null);
  const inputRef = useRef(null);

  const patch = (key, fields) => setItems(prev => prev.map(i => (i.key === key ? { ...i, ...fields } : i)));

  const addFiles = async (fileList) => {
    setNote(null);
    const files = [...(fileList || [])].filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (!files.length) { setNote('Only pictures and videos can be added here.'); return; }
    const room = MAX_MEDIA - items.length;
    if (room <= 0) { setNote(`You can add up to ${MAX_MEDIA} pictures and videos.`); return; }
    if (files.length > room) setNote(`Only the first ${room} were added. Up to ${MAX_MEDIA} per post.`);

    for (const file of files.slice(0, room)) {
      const kind = file.type.startsWith('video/') ? 'video' : 'image';
      const key = `m${++keySeq}`;
      if (kind === 'video') {
        const secs = await videoLength(file);
        if (secs && secs > MAX_VIDEO_SECONDS + 2) {
          const m = Math.floor(secs / 60); const s = Math.round(secs % 60);
          setNote(`"${file.name}" is ${m}:${String(s).padStart(2, '0')} long. Videos can be up to 3 minutes. Trim it and try again.`);
          continue;
        }
      }
      // No browser-side preview (the site's security rules block blob: pictures); the tile shows once the upload is back.
      setItems(prev => [...prev, { key, kind, name: file.name, status: 'uploading', progress: 0, alt_text: '' }]);
      uploadMedia(file, (p) => patch(key, { progress: p }))
        .then(m => patch(key, { status: 'done', id: m.id, url: m.url }))
        .catch(err => patch(key, { status: 'error', error: err.response?.data?.error || 'Could not upload this one.' }));
    }
  };

  const remove = (key) => setItems(prev => prev.filter(i => i.key !== key));

  const onPaste = (e) => {
    const files = [...(e.clipboardData?.items || [])].filter(i => i.kind === 'file').map(i => i.getAsFile()).filter(Boolean);
    if (files.length) { e.preventDefault(); addFiles(files); }
  };

  return (
    <div onPaste={onPaste}>
      {items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          {items.map(item => (
            <div key={item.key} className="flex gap-2 items-start border border-gray-200 rounded-lg p-2 bg-white">
              <div className="relative w-16 h-16 shrink-0 rounded-md overflow-hidden bg-gray-100">
                {item.url && (item.kind === 'video'
                  ? <video src={`${item.url}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  : <img src={item.url} alt="" className="w-full h-full object-cover" />)}
                {item.kind === 'video' && <span className="absolute bottom-0.5 left-0.5 text-[10px] bg-black/60 text-white rounded px-1">▶ video</span>}
                {item.status === 'uploading' && (
                  <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-[11px] font-medium text-reset-brown">{item.progress}%</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                {item.status === 'error' ? (
                  <p className="text-[11px] text-red-600">{item.error}</p>
                ) : (
                  <input
                    value={item.alt_text}
                    onChange={e => patch(item.key, { alt_text: e.target.value })}
                    maxLength={500}
                    placeholder={item.kind === 'video' ? 'Describe this video (optional)' : 'Describe this picture (optional)'}
                    aria-label={item.kind === 'video' ? 'Describe this video' : 'Describe this picture'}
                    className="w-full border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                  />
                )}
                <button type="button" onClick={() => remove(item.key)} className="text-[11px] text-gray-400 hover:text-red-600 mt-1">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {items.length < MAX_MEDIA && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
          className={`border-2 border-dashed rounded-lg px-3 ${compact ? 'py-2' : 'py-3'} text-center text-xs cursor-pointer transition-colors
            ${dragging ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
          <span className="text-gray-500">
            📷 Add pictures or videos{items.length ? ` (${items.length} of ${MAX_MEDIA})` : ''}
          </span>
          {!compact && <span className="block text-[11px] text-gray-400 mt-0.5">Up to {MAX_MEDIA}. Videos up to 3 minutes. Drag, paste or tap to choose.</span>}
        </div>
      )}
      {note && <p className="text-[11px] text-reset-brown mt-1">{note}</p>}
    </div>
  );
}
